import { LitElement, PropertyValues, html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-checkbox.styles';
import { SDS_SIZES } from '../types';
import { SDSDelegateInteractionMixin, SDSEventDispatcherMixin } from '../mixins';

type CheckboxSize = Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;

export interface SDSCheckboxProps {
	name?: string;
	label?: string;
	value?: string;
	checked?: boolean;
	disabled?: boolean;
	error?: boolean;
	required?: boolean;
	readonly?: boolean;
	size?: CheckboxSize;
	'help-text'?: string;
	parentRequestDisabled?: boolean;
}

@guardedCustomElement('sds-checkbox')
export class SDSCheckbox extends SDSDelegateInteractionMixin(SDSEventDispatcherMixin(LitElement)) {
	// Styling
	static override styles = [styles];

	static formAssociated = true;
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	// Props
	@property({ type: String, reflect: true }) name?: string;
	@property({ type: String, reflect: true }) label?: string;
	@property({ type: String }) value?: string;
	@property({ type: Boolean, reflect: true }) checked = false;

	/**
	 * disabled:
	 * - If set directly (prop/attr), this checkbox becomes "self-controlled"
	 *   and ignores parentRequestDisabled.
	 * - If not self-controlled, this follows parentRequestDisabled (group/form).
	 */
	@property({ type: Boolean, reflect: true })
	get disabled(): SDSCheckboxProps['disabled'] {
		return this._disabled;
	}
	set disabled(value: SDSCheckboxProps['disabled']) {
		const newVal = value ?? false;
		const oldVal = this._disabled;

		if (oldVal === newVal) return;

		this._disabled = newVal;
		this.requestUpdate('disabled', oldVal);

		if (!this._settingDisabledFromParent) {
			this._disabledIsAuthorControlled = newVal;
		}
	}

	@property({ type: Boolean, reflect: true }) error = false;
	@property({ type: Boolean, reflect: true }) required = false;
	@property({ type: String, reflect: true }) size: CheckboxSize = 'md';
	@property({ type: String, attribute: 'help-text' }) helpText?: string;
	@property({ type: Boolean, attribute: 'readonly', reflect: true }) readOnly: boolean = false;

	/**
	 * parentRequestDisabled:
	 * - Set by SDSCheckboxGroup to request disabled/enabled.
	 * - Only applied if SDSCheckbox is NOT self-controlled.
	 */
	@property({ type: Boolean, attribute: false })
	get parentRequestDisabled(): SDSCheckboxProps['parentRequestDisabled'] {
		return this._parentRequestDisabled;
	}
	set parentRequestDisabled(value: SDSCheckboxProps['parentRequestDisabled']) {
		const newVal = value ?? false;
		const oldVal = this._parentRequestDisabled;

		if (oldVal === newVal) return;

		this._parentRequestDisabled = newVal;
		this.requestUpdate('parentRequestDisabled', oldVal);

		if (this._disabledIsAuthorControlled) return;

		this._settingDisabledFromParent = true;
		this.disabled = newVal;
		this._settingDisabledFromParent = false;
	}

	@query('#control') control!: HTMLInputElement;

	private _disabled: SDSCheckboxProps['disabled'] = false;
	private _disabledIsAuthorControlled = false;
	private _settingDisabledFromParent = false;
	private _parentRequestDisabled: SDSCheckboxProps['parentRequestDisabled'] = false;
	private _initialChecked: boolean = false;

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override connectedCallback() {
		super.connectedCallback();
		this._initialChecked = this.checked;
		this.internals.role = 'checkbox';

		if (this.checked) {
			this.internals.setFormValue(this.value ?? 'on');
		}
	}

	override firstUpdated(_changedProperties: PropertyValues): void {
		super.firstUpdated(_changedProperties);
		if (this.control) {
			this.control.required = this.required;
		}
		this._validate();
	}

	formResetCallback() {
		this.checked = this._initialChecked;
		if (this.control) {
			this.control.checked = this._initialChecked;
		}
		const resetValue = this.checked ? this.value ?? 'on' : null;
		this.internals.setFormValue(resetValue);
		this._validate();
	}

	private _validate() {
		if (this.required && !this.checked) {
			this.internals.setValidity({ valueMissing: true }, this.control.validationMessage, this.control);
		} else {
			this.internals.setValidity({});
		}
	}

	private readonly _handleChange = (event: Event) => {
		event.stopImmediatePropagation();
		if (this.disabled || this.readOnly) {
			(event.target as HTMLInputElement).checked = this.checked;
			event.preventDefault();
			return;
		}

		const elem = event.target as HTMLInputElement;
		this.checked = elem.checked;
		this.internals.setFormValue(elem.checked ? elem.value : null);
		this._validate();
		this.dispatchSheriffEvent('change');
	};

	private readonly _handleFocus = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.dispatchSheriffEvent('focus');
	};

	private readonly _handleBlur = (event: FocusEvent) => {
		event.stopImmediatePropagation();
		this.dispatchSheriffEvent('blur');
	};

	override updated(changedProperties: Map<string | number | symbol, unknown>) {
		super.updated(changedProperties);
		if (changedProperties.has('required') && this.control) {
			this.control.required = this.required;
		}
		if (changedProperties.has('checked')) {
			this._validate();
		}

		if (!this._disabledIsAuthorControlled && this.disabled !== this._parentRequestDisabled) {
			this._settingDisabledFromParent = true;
			this.disabled = this._parentRequestDisabled ?? false;
			this._settingDisabledFromParent = false;
		}
	}

	get type(): string {
		return 'checkbox';
	}

	override render() {
		const checkboxClasses = {
			'sds-checkbox': true,
			'sds-checkbox--error': Boolean(this.error),
		};

		const inputClasses = {
			'sds-checkbox__input': true,
			'sds-checkbox__input--error': Boolean(this.error),
		};

		return html`
			<div class="${classMap(checkboxClasses)}">
				<input
					id="control"
					type="checkbox"
					name="${ifDefined(this.name)}"
					value="${ifDefined(this.value)}"
					.checked="${this.checked}"
					?checked="${this.checked}"
					?disabled="${this.disabled}"
					?required="${this.required}"
					?readonly="${this.readOnly}"
					@change="${this._handleChange}"
					@focus="${this._handleFocus}"
					@blur="${this._handleBlur}"
					class="${classMap(inputClasses)}"
				/>
				${this.label ? html`<label for="control" class="sds-checkbox__label">${this.label}</label>` : nothing}
				${this.helpText ? html`<span class="sds-checkbox__help-text">${this.helpText}</span>` : nothing}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-checkbox': SDSCheckbox;
	}
}
